"use client";

import { Moon } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeSwitcher } from "../theme-switcher";
import { renderUserMenuItems, useUserMenuItems } from "./user-menu-items";

interface UserMenuProps {
  userName: string;
  userInitials: string;
  organizationName?: string;
}

export function UserMenu({
  userName,
  userInitials,
  organizationName,
}: UserMenuProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);

  // Use the hook version that supports feature flags
  const menuItems = useUserMenuItems();

  // Check if on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close the drawer when an item is clicked
  const handleItemSelect = () => {
    setOpen(false);
  };

  return isMobile ? (
    // Mobile version: Bottom Drawer
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="px-4 pt-2 pb-6">
        <DrawerHeader className="p-0 mb-4">
          <div className="flex items-center gap-3 py-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
            <div>
              <DrawerTitle className="text-base font-semibold">
                {userName}
              </DrawerTitle>
              {organizationName && (
                <p className="text-sm text-muted-foreground">
                  {organizationName}
                </p>
              )}
            </div>
          </div>
        </DrawerHeader>

        <div className="space-y-2">
          {renderUserMenuItems({
            items: menuItems,
            onSelect: handleItemSelect,
            renderItem: (item, index) => (
              <Button
                key={`drawer-item-${index}`}
                variant="ghost"
                className="w-full justify-start h-11 px-2 text-base"
                asChild={!!item.href}
                onClick={item.onClick}
              >
                {item.href ? (
                  <Link
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ) : (
                  <>
                    {item.icon}
                    {item.label}
                  </>
                )}
              </Button>
            ),
          })}
          <div className="mt-2">
            <div className="flex items-center justify-between rounded-lg p-2 hover:bg-muted">
              <div className="flex items-center">
                <Moon className="mr-2 h-4 w-4" />
                <span>Theme</span>
              </div>
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    // Desktop version: Dropdown Menu
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userName}</p>
            {organizationName && (
              <p className="text-xs leading-none text-muted-foreground">
                {organizationName}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {renderUserMenuItems({
          items: menuItems,
          renderItem: (item, index) =>
            item.href ? (
              <DropdownMenuItem key={`dropdown-item-${index}`} asChild>
                <Link
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                >
                  {item.icon}
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ) : item.label === "Sign Out" ? (
              <>
                <DropdownMenuItem
                  key="theme-switcher-item"
                  className="flex items-center justify-between"
                  onSelect={(e) => e.preventDefault()}
                >
                  <div className="flex items-center">
                    <Moon className="mr-2 h-4 w-4" />
                    <span>Theme</span>
                  </div>
                  <ThemeSwitcher />
                </DropdownMenuItem>
                <DropdownMenuItem
                  key={`dropdown-item-${index}`}
                  onClick={item.onClick}
                >
                  {item.icon}
                  {item.label}
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                key={`dropdown-item-${index}`}
                onClick={item.onClick}
              >
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            ),
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
